// Everything the success page + certificate need, assembled at submit time from
// the wizard forms, the resolved election/party Redux data, and the persisted
// Volunteer entity returned by POST /Volunteer. Kept flat so it survives a
// JSON round-trip through sessionStorage / router navigation state.
export interface SuccessPayload {
	firstName: string;
	lastName: string;
	email: string;
	phoneNumber: string;
	electionName?: string;
	electionDate?: string;
	partyName?: string;
	partyLongName?: string;
	pollingUnitName?: string;
	pollingUnitCode?: string;
	role?: string;
	// Server truth only: copied from the create response, never from the URL.
	isTrustedAgent?: boolean;
	// Set only when the server echoed `location.accepted === false` — the
	// application itself went through; just the GPS fix was not usable.
	locationNote?: string;
	id: number;
	verificationToken?: string;
	dateCreated?: string;
	statusId?: number;
	reference: string;
}

// A refresh fallback: the page prefers router state, then reads this.
export const SUCCESS_STORAGE_KEY = 'ielect-application-success';

export const storeSuccessPayload = (payload: SuccessPayload): void => {
	try {
		sessionStorage.setItem(SUCCESS_STORAGE_KEY, JSON.stringify(payload));
	} catch {
		// Storage can throw in private mode / when full — the page still works
		// off router state within the same navigation, so degrade silently.
	}
};

export const readSuccessPayload = (): SuccessPayload | null => {
	try {
		const raw = sessionStorage.getItem(SUCCESS_STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as SuccessPayload;
		return parsed?.id ? parsed : null;
	} catch {
		return null;
	}
};

// Human-readable reference derived from persisted ids so it stays reconcilable
// server-side (electionId + volunteer id), zero-padded for a credential look.
export const buildReference = (electionId?: number, volunteerId?: number): string =>
	`IV-${electionId ?? '0'}-${String(volunteerId ?? 0).padStart(5, '0')}`;

// The persisted-entity fields the create response supplies (see the
// volunteer-create-response contract).
export interface CreatedVolunteer {
	id: number;
	verificationToken?: string;
	dateCreated?: string;
	volunteerStatusId?: number;
	// Echoed by newer backends for applications made on the trusted link.
	isTrustedAgent?: boolean;
	// Echoed by newer backends: whether the submitted GPS fix was accepted and,
	// when not, a short machine reason (e.g. `outside_nigeria`).
	location?: { accepted?: boolean; reason?: string | null; distanceToEntityMeters?: number | null } | null;
}

const humaniseReason = (reason: string | null | undefined): string => {
	const text = (reason ?? '').replace(/[_-]+/g, ' ').trim().toLowerCase();
	return text ? ` (${text})` : '';
};

/** "Your location couldn’t be used (outside nigeria). …" or undefined when accepted/unknown. */
export const locationNoteFrom = (created: CreatedVolunteer): string | undefined =>
	created.location?.accepted === false
		? `Your location couldn’t be used${humaniseReason(created.location.reason)}. Your application was still received.`
		: undefined;

// The wizard/Redux context the success payload is assembled from at submit time.
export interface SuccessPayloadSources {
	personal: { firstName?: string; lastName?: string; email?: string; phoneNumber?: string };
	election?: { id?: number; name?: string; date?: string };
	party?: { shortName?: string; longName?: string };
	location?: { entityName?: string; entityCode?: string };
	role?: string;
}

/**
 * Assembles the SuccessPayload from the create response plus the wizard/Redux
 * context. Extracted from the orchestrator so the submit→success wiring is
 * unit-testable without mounting the full wizard.
 */
export const buildSuccessPayload = (created: CreatedVolunteer, src: SuccessPayloadSources): SuccessPayload => ({
	firstName: src.personal.firstName ?? '',
	lastName: src.personal.lastName ?? '',
	email: src.personal.email ?? '',
	phoneNumber: src.personal.phoneNumber ?? '',
	electionName: src.election?.name,
	electionDate: src.election?.date,
	partyName: src.party?.shortName,
	partyLongName: src.party?.longName,
	pollingUnitName: src.location?.entityName,
	pollingUnitCode: src.location?.entityCode,
	role: src.role,
	// Only the server's explicit `true` counts; absent/false → undefined so the
	// success page and certificate never print "trusted" off the URL alone.
	isTrustedAgent: created.isTrustedAgent === true ? true : undefined,
	locationNote: locationNoteFrom(created),
	id: created.id,
	verificationToken: created.verificationToken,
	dateCreated: created.dateCreated,
	statusId: created.volunteerStatusId,
	reference: buildReference(src.election?.id, created.id),
});

// resultType (EC8A…EC8D) → the role title shown on the page + certificate.
const ROLE_LABELS: Record<string, string> = {
	EC8A: 'Polling Unit Collation Agent',
	EC8B: 'Ward Collation Officer',
	EC8C: 'Local Government Collation Officer',
	EC8D: 'State Collation Officer',
};

export const roleLabel = (role?: string): string => (role ? (ROLE_LABELS[role] ?? 'Election Volunteer') : 'Election Volunteer');

export const fullName = (payload: Pick<SuccessPayload, 'firstName' | 'lastName'>): string =>
	`${payload.firstName ?? ''} ${payload.lastName ?? ''}`.trim();

export const formatSubmittedDate = (iso?: string): string => {
	if (!iso) return '';
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return '';
	return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

// Maps a backend volunteerStatusId to a display label + tone for the status pill.
// Ids per api.core VolunteerStatusIds (1 = ApplicationReceived at creation).
export type StatusTone = 'received' | 'progress' | 'success' | 'rejected';

export interface StatusMeta {
	label: string;
	tone: StatusTone;
}

const STATUS_META: Record<number, StatusMeta> = {
	1: { label: 'Application received', tone: 'received' },
	2: { label: 'Approved', tone: 'success' },
	3: { label: 'Not selected', tone: 'rejected' },
	4: { label: 'Offer sent', tone: 'progress' },
	5: { label: 'Offer expired', tone: 'rejected' },
	6: { label: 'Under review', tone: 'progress' },
	7: { label: 'Reserved', tone: 'progress' },
	8: { label: 'Confirmed agent', tone: 'success' },
};

export const statusMeta = (statusId?: number | null, statusText?: string | null): StatusMeta => {
	if (statusId != null && STATUS_META[statusId]) return STATUS_META[statusId];
	if (statusText) return { label: statusText, tone: 'progress' };
	return { label: 'Application received', tone: 'received' };
};
